// Central catalog of every live JupyterHub site+version CAMCEADHUB can
// route requests to. Added 2026-09-11 for the ARCUS/Imperial/STFC
// multi-site build - see CAMCEADHUB_SYSTEM_README.md.
//
// UX split (2026-09-11, revised same day per direct feedback): the
// JupyterHub *version* (3 vs 4) is a Profile setting (ProfileEdit.vue) -
// it rarely changes and isn't tied to what you're about to spawn. The
// *site* (ARCUS/Imperial/STFC) is chosen on the spawn page itself
// (JupyterLab.vue), alongside server type/image/GPU, since that's a
// per-spawn decision - picking STFC there defaults "Request a GPU" on,
// since STFC is the GPU site.
//
// Both pieces combine into one hub id, which is what actually gets sent
// as the `hub` (servers/token/progress/etc) and `hub_version` (images) API
// params - must match the camceadhub backend's camceadhub/jhub/utils.py
// `_HUB_PREFIXES` keys exactly, or the backend silently falls back to
// ARCUS v3 (get_hub_prefix()'s default).
export const HUB_SITES = [
  { id: "arcus", label: "ARCUS (Cambridge)", defaultGpu: false },
  { id: "imperial", label: "Imperial", defaultGpu: false },
  { id: "stfc", label: "STFC (GPU)", defaultGpu: true },
];

export const HUB_VERSIONS = [
  { id: "3", label: "Jupyter 3" },
  { id: "4", label: "Jupyter 4" },
];

export const DEFAULT_SITE_ID = "arcus";
export const DEFAULT_VERSION_ID = "3";

// `urlPrefix` is the path segment under camceadhub.ast.cam.ac.uk that
// JupyterLabView.vue builds the live notebook iframe URL from - "" for
// ARCUS v3 (that hub lives at the domain root, no base_url override),
// "<prefix>/" for every other hub, matching that hub's own
// `c.JupyterHub.base_url` and apm62 nginx's own location block of the
// same name. Keyed by the combined hub id (see hubId() below).
const HUB_URL_PREFIXES = {
  "3": "",
  // Fixed 2026-09-11: the real deployed base_url on ARCUS v4 has always
  // actually been "/jhub-v4/", not "/jhub/" - confirmed directly from the
  // live hub Secret. See the camceadhub backend's utils.py for the matching fix
  // and the full story (a 302 redirect loop, the first time anything
  // actually exercised this path end-to-end).
  "4": "jhub-v4/",
  "imperial-v3": "jhub-imperial-v3/",
  "imperial-v4": "jhub-imperial-v4/",
  "stfc-v3": "jhub-stfc-v3/",
  "stfc-v4": "jhub-stfc-v4/",
};

// Combines a site + version into the hub id camceadhub backend expects.
// ARCUS keeps the old bare "3"/"4" ids (backward compatible with
// pre-existing localStorage values and the backend's own default), every
// other site is "<site>-v<version>".
export function hubId(siteId, versionId) {
  const version = versionId || DEFAULT_VERSION_ID;
  if (!siteId || siteId === "arcus") return version;
  return `${siteId}-v${version}`;
}

// Inverse of hubId() - given a stored/combined hub id (which is all that
// used to be persisted, under different schemes at different points),
// recover {site, version}. Used once, at store init, to migrate whatever
// a user's browser already has stored into the new split site/version
// state, regardless of which scheme it was written under.
export function siteAndVersionFromHubId(id) {
  if (!id || id === "3" || id === "4") {
    return { site: DEFAULT_SITE_ID, version: id || DEFAULT_VERSION_ID };
  }
  const match = /^(imperial|stfc)-v(3|4)$/.exec(id);
  if (match) return { site: match[1], version: match[2] };
  return { site: DEFAULT_SITE_ID, version: DEFAULT_VERSION_ID };
}

export function hubUrlPrefix(id) {
  return HUB_URL_PREFIXES[id] ?? "";
}

export function siteLabel(siteId) {
  const site = HUB_SITES.find((s) => s.id === siteId);
  return site ? site.label : HUB_SITES[0].label;
}

export function siteDefaultGpu(siteId) {
  const site = HUB_SITES.find((s) => s.id === siteId);
  return site ? site.defaultGpu : false;
}
